import { FilterDocument, GoalDeadlineRowsMap } from '@griply/types';
import { onNoGroupingReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo } from 'react';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTreeValue,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { RelationshipUpdate } from 'shared/types/relationship';
import { Task, UpdateTask } from 'shared/types/task';
import { DueDisplayOptions } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';

import {
  NoGroupingRenderData,
  NoGroupingRenderDataContext,
} from './no-grouping-render-context';
import { NoGroupingRenderItem } from './no-grouping-render-item';
import * as Styled from './no-grouping-tasks-list.style';

export type NoGroupingTaskListRendererProps = {
  treeData: Record<ID, TreeItem>;
  tasks: Task[];
  onReorder?: (
    updatedSections: { id: ID; taskSorting: ID[] }[],
    updatedTask: UpdateTask[],
    relationshipUpdates?: RelationshipUpdate[],
  ) => void;
  collapsedSections?: ID[];
  onSectionCollapse?: (sectionIds: ID[]) => void;
  scrollElement?: React.RefObject<HTMLDivElement>;
  denseFormat?: boolean;
  filterDocument?: FilterDocument;
  isContextMenuOpen?: boolean;
  defaultNewTaskDate?: Timestamp;
  goalId?: ID;
  lifeAreaId?: ID;
  tagId?: ID;
  displayDue?: DueDisplayOptions[];
  showRepeatingAsTodo?: boolean;
  hideNewTaskRow?: boolean;
  goalRenderData?: GoalDeadlineRowsMap;
};

const taskItemTypes = [TaskListItemTypes.Task];

const dropTypes = {
  [TaskListItemTypes.Root]: [TaskListItemTypes.Task],
  [TaskListItemTypes.Goal]: [],
  [TaskListItemTypes.GoalDeadline]: [],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.LifeArea]: [],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
};

export const NoGroupingTaskListRenderer: React.FC<
  NoGroupingTaskListRendererProps
> = ({
  treeData,
  tasks,
  onReorder,
  collapsedSections,
  onSectionCollapse,
  scrollElement,
  denseFormat,
  filterDocument,
  isContextMenuOpen,
  defaultNewTaskDate,
  goalId,
  lifeAreaId,
  tagId,
  displayDue,
  showRepeatingAsTodo,
  hideNewTaskRow,
  goalRenderData,
}) => {
  const today = useToday();

  const tasksMap = useMemo(() => {
    return tasks.reduce<Record<ID, Task>>((acc, task) => {
      acc[task.id] = task;
      return acc;
    }, {});
  }, [tasks]);

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }
    const result = onNoGroupingReorder({ updatedItems });
    if (!result) {
      return;
    }
    onReorder(result.updatedSections, result.updatedTasks);
  };

  const renderData = useMemo<NoGroupingRenderData>(
    () => ({
      tasksMap,
      tasks,
      today,
      filterDocument,
      defaultNewTaskDate,
      goalId,
      lifeAreaId,
      tagId,
      displayDue,
      showRepeatingAsTodo,
      hideNewTaskRow,
      goalRenderData,
    }),
    [
      tasksMap,
      tasks,
      today,
      filterDocument,
      defaultNewTaskDate,
      goalId,
      lifeAreaId,
      tagId,
      displayDue,
      showRepeatingAsTodo,
      hideNewTaskRow,
      goalRenderData,
    ],
  );

  const tree = useTreeValue();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <NoGroupingRenderDataContext.Provider value={renderData}>
        <VirtualizedTreeList
          rootItemId={rootId}
          items={treeData}
          renderItem={NoGroupingRenderItem}
          scrollElement={scrollElement}
          multiselectTypes={taskItemTypes}
          dragTypes={taskItemTypes}
          onReorder={_onReorder}
          dropTypes={dropTypes}
          collapsedItems={collapsedSections}
          onCollapse={onSectionCollapse}
          denseFormat={denseFormat}
          isContextMenuOpen={isContextMenuOpen}
        />
      </NoGroupingRenderDataContext.Provider>
    </Styled.Container>
  );
};
